import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import ArchiveIcon from '@mui/icons-material/Archive';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import MDEditor from '@uiw/react-md-editor';
import {
  supportAgentApi,
  KbDocListItem,
  KbDocDetail,
  KbAudience,
  KbDocSource,
  KbDocSourceType,
  KbDocWritableStatus,
  KbDocStatus,
  KbConflictCheck,
  KbConflictDispositionAction,
  KB_CONFLICT_TERMINAL_STATES,
  contentTypeForFilename,
  uploadFileToPresignedUrl,
  isPendingExtraction,
  KB_UPLOAD_ALLOWED_TYPES,
  kbUploadSizeError,
} from './support-agent-api';
import { KB_TEMPLATES } from './kb-templates';
import { VersionHistoryDrawer, VersionHistoryEntry } from './version-history-drawer';
import { ConflictCheckUserError, KbConflictCheckDrawer, KbConflictJumpTarget, describeConflictCheckError } from './kb-conflict-check-drawer';
import { isKbConflictCheckEnabled } from './kb-conflict-check-flag';

const STATUS_COLOR: Record<KbDocStatus, 'default' | 'success' | 'warning'> = {
  draft: 'warning',
  published: 'success',
  archived: 'default',
};

const UPLOAD_ACCEPT = KB_UPLOAD_ALLOWED_TYPES.map((t) => `.${t}`).join(',');

// Conflict check polling (kb-conflict-check-drawer.tsx): the Lambda's worker
// typically finishes a short article in under a minute; the run-level
// deadline on its side is ~10 minutes, so stop asking after 15.
const CONFLICT_POLL_INTERVAL_MS = 2_000;
const CONFLICT_POLL_MAX_MS = 15 * 60_000;

const AUDIENCE_LABEL: Record<KbAudience, string> = {
  both: 'Both',
  app: 'POM Safe mobile app',
  safety_hub: 'Safety Hub web app',
};
// A doc with no stored audience is treated as 'both' everywhere.
const DEFAULT_AUDIENCE: KbAudience = 'both';

const SOURCE_TYPE_LABEL: Record<KbDocSourceType, string> = {
  zendesk: 'Zendesk help-center article',
  code: 'Derived from the product code',
  manual: 'Written by hand',
};

/**
 * Select the quoted span in the markdown editor so the reviewer lands on it.
 * @uiw/react-md-editor renders a plain <textarea class="w-md-editor-text-input">;
 * best effort — if the DOM shape changes this silently does nothing.
 */
export function selectDraftRange(target: KbConflictJumpTarget): boolean {
  const textarea = document.querySelector<HTMLTextAreaElement>('.w-md-editor-text-input');
  if (!textarea) return false;
  textarea.focus();
  textarea.setSelectionRange(target.start, target.end);
  // Browsers do not reliably scroll a textarea's content to a selection set
  // after focus; a blur/focus cycle after selecting does.
  textarea.blur();
  textarea.focus();
  textarea.scrollIntoView?.({ block: 'center' });
  return true;
}

/**
 * A textarea reads CRLF back as LF, and the conflict check's quote offsets
 * index whatever body we send — so every body that enters the editor is
 * normalized first, and the checked string is the one the editor holds.
 */
export function normalizeEditorBody(body: string): string {
  return body.replace(/\r\n?/g, '\n');
}

function stripExtension(filename: string): string {
  const idx = filename.lastIndexOf('.');
  return idx > 0 ? filename.slice(0, idx) : filename;
}

type View = { mode: 'list' } | { mode: 'edit'; id: string | null };

/**
 * Knowledge base panel (design doc §6.7 panel 1, §6.9 v1 authoring spec).
 * List + editor in one component, switched by local `view` state — keeps
 * the "back to list" flow simple without adding a router sub-route.
 */
export const KnowledgeBasePanel: React.FC<{ initialDocId?: string; onLeaveInitialDoc?: () => void }> = ({
  initialDocId,
  onLeaveInitialDoc,
}) => {
  // `initialDocId`: a link straight to one article (?kbDoc=<id>) — the Chats
  // tab's citation links open the article an assistant reply used. Leaving
  // that article tells the page (`onLeaveInitialDoc`) so the link's param
  // goes and a refresh or tab switch doesn't reopen it.
  const [view, setView] = useState<View>(() => (initialDocId ? { mode: 'edit', id: initialDocId } : { mode: 'list' }));
  useEffect(() => {
    if (initialDocId && !(view.mode === 'edit' && view.id === initialDocId)) onLeaveInitialDoc?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // ── list state ──────────────────────────────────────────────────────
  const [items, setItems] = useState<KbDocListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // TablePagination is 0-indexed; API is 1-indexed
  const [limit, setLimit] = useState(25);
  const [q, setQ] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [templateMenuAnchor, setTemplateMenuAnchor] = useState<null | HTMLElement>(null);

  const loadList = async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await supportAgentApi.listKbDocs({ q: q || undefined, page: page + 1, limit });
      setItems(res.items);
      setTotal(res.total);
    } catch (err: any) {
      setListError(err?.response?.data?.message || err.message);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
  }, [q]);

  useEffect(() => {
    if (view.mode !== 'list') return;
    const t = setTimeout(loadList, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [view.mode, page, limit, q]);

  // ── editor state ────────────────────────────────────────────────────
  const [doc, setDoc] = useState<KbDocDetail | null>(null);
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState<KbAudience>(DEFAULT_AUDIENCE);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState<KbDocWritableStatus | null>(null);
  const [versionDrawerOpen, setVersionDrawerOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState<number | null>(null);
  const [restoreSnackbarOpen, setRestoreSnackbarOpen] = useState(false);
  // Provenance (write-only in v1): stored with the doc when set.
  const [sourceType, setSourceType] = useState<KbDocSourceType | ''>('');
  const [sourceRef, setSourceRef] = useState('');

  // ── conflict check state (kb-conflict-check-drawer.tsx) ────────────────
  const [conflictDrawerOpen, setConflictDrawerOpen] = useState(false);
  const [conflictCheckId, setConflictCheckId] = useState<string | null>(null);
  const [conflictCheck, setConflictCheck] = useState<KbConflictCheck | null>(null);
  const [conflictLoading, setConflictLoading] = useState(false);
  const [conflictError, setConflictError] = useState<string | null>(null);
  const [conflictStartedAt, setConflictStartedAt] = useState<number | null>(null);
  const [conflictElapsed, setConflictElapsed] = useState(0);
  const [dispositionBusy, setDispositionBusy] = useState<string | null>(null);
  // Bumped on every reset and every new check. A POST or disposition that
  // resolves after the reviewer moved to another document compares its run
  // against the current one and drops its result, so one article's findings
  // never land on another.
  const conflictRun = useRef(0);
  const conflictInFlight = useRef(false);

  const resetConflictState = () => {
    conflictRun.current += 1;
    conflictInFlight.current = false;
    setConflictCheck(null);
    setConflictCheckId(null);
    setConflictLoading(false);
    setConflictError(null);
    setConflictDrawerOpen(false);
    setDispositionBusy(null);
  };

  // ── upload state (list view) ───────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (view.mode !== 'edit' || !view.id) return;
    const id = view.id;
    (async () => {
      setEditorLoading(true);
      setEditorError(null);
      try {
        const d = await supportAgentApi.getKbDoc(id);
        setDoc(d);
        setTitle(d.title);
        setTags(d.tags);
        setBody(normalizeEditorBody(d.body));
        setAudience(d.audience ?? DEFAULT_AUDIENCE);
        setSourceType(d.source?.type ?? '');
        setSourceRef(d.source?.ref ?? '');
      } catch (err: any) {
        setEditorError(err?.response?.data?.message || err.message);
      } finally {
        setEditorLoading(false);
      }
    })();
  }, [view]);

  const openNewDoc = (templateBody: string) => {
    setDoc(null);
    setTitle('');
    setTags([]);
    setBody(templateBody);
    setAudience(DEFAULT_AUDIENCE);
    setSourceType('');
    setSourceRef('');
    resetConflictState();
    setEditorError(null);
    setSaveNotice(null);
    setTemplateMenuAnchor(null);
    setView({ mode: 'edit', id: null });
  };

  const openEditDoc = (id: string) => {
    setDoc(null);
    setEditorError(null);
    setSaveNotice(null);
    resetConflictState();
    setView({ mode: 'edit', id });
  };

  // `source` absent from the payload = keep what is stored; explicit null = clear.
  // kb.service.ts replaces the object wholesale, so only send it when the
  // editor changed something, and carry over fields this form does not edit
  // (`commit`) when the type is unchanged.
  const sourcePayload = (): { source?: KbDocSource | null } => {
    const stored = doc?.source ?? null;
    if (!sourceType) return stored ? { source: null } : {};
    const ref = sourceRef.trim();
    const next: KbDocSource = {
      ...(stored && stored.type === sourceType ? stored : {}),
      type: sourceType,
      ...(ref ? { ref } : {}),
    };
    if (!ref) delete next.ref;
    if (stored && stored.type === next.type && (stored.ref ?? '') === (next.ref ?? '') && (stored.commit ?? '') === (next.commit ?? '')) {
      return {};
    }
    return { source: next };
  };

  // Only send `audience` when it differs from what is stored (a missing value
  // counts as 'both'), so saving an untagged doc does not write one.
  const audiencePayload = (): { audience?: KbAudience } =>
    audience === (doc?.audience ?? DEFAULT_AUDIENCE) ? {} : { audience };

  const backToList = () => setView({ mode: 'list' });

  const handleSave = async (status: KbDocWritableStatus) => {
    setSaving(status);
    setEditorError(null);
    try {
      if (doc) {
        const updated = await supportAgentApi.updateKbDoc(doc.id, { title, tags, body, status, ...audiencePayload(), ...sourcePayload() });
        setDoc(updated);
      } else {
        const { source } = sourcePayload();
        const created = await supportAgentApi.createKbDoc({ title, tags, body, status, ...audiencePayload(), ...(source ? { source } : {}) });
        setDoc(created);
        setView({ mode: 'edit', id: created.id });
      }
      setSaveNotice(status === 'published' ? 'Published — live for the next ticket.' : 'Saved as draft.');
    } catch (err: any) {
      setEditorError(err?.response?.data?.message || err.message);
    } finally {
      setSaving(null);
    }
  };

  const handleArchive = async () => {
    if (!doc) return;
    setArchiving(true);
    try {
      await supportAgentApi.archiveKbDoc(doc.id);
      setArchiveConfirmOpen(false);
      backToList();
    } catch (err: any) {
      setEditorError(err?.response?.data?.message || err.message);
      setArchiveConfirmOpen(false);
    } finally {
      setArchiving(false);
    }
  };

  // Restore loads the full snapshot (GET /kb/:id/versions/:index) into the
  // editor as UNSAVED changes — it never writes directly. The doc's `id`
  // and persisted `status`/`versions` are left alone; only local
  // title/tags/body change, so Save as draft / Publish is still required
  // to actually restore anything, and doing so creates a new version from
  // whatever was on the server before (per the normal save flow).
  const handleRestoreVersion = async (index: number) => {
    if (!doc) return;
    setRestoring(index);
    setEditorError(null);
    try {
      const snapshot = await supportAgentApi.getKbDocVersion(doc.id, index);
      setTitle(snapshot.title);
      setTags(snapshot.tags);
      setBody(normalizeEditorBody(snapshot.body));
      // A snapshot without an audience (older, or written by the extraction pipeline) leaves the current choice alone.
      if (snapshot.audience) setAudience(snapshot.audience);
      setVersionDrawerOpen(false);
      setRestoreSnackbarOpen(true);
    } catch (err: any) {
      setEditorError(err?.response?.data?.message || err.message);
    } finally {
      setRestoring(null);
    }
  };

  // ── conflict check: POST the unsaved draft, then poll until terminal ─────
  const handleCheckConflicts = async () => {
    if (conflictInFlight.current) return;
    conflictInFlight.current = true;
    const run = ++conflictRun.current;
    setConflictDrawerOpen(true);
    setConflictError(null);
    setConflictCheck(null);
    setConflictCheckId(null);
    setConflictLoading(true);
    setConflictStartedAt(Date.now());
    setConflictElapsed(0);
    setDispositionBusy(null);
    try {
      const { checkId } = await supportAgentApi.createKbConflictCheck({
        ...(doc ? { docId: doc.id } : {}),
        title,
        body,
      });
      if (run !== conflictRun.current) return; // reviewer moved on; this check belongs to another document
      setConflictCheckId(checkId);
    } catch (err: any) {
      if (run !== conflictRun.current) return;
      conflictInFlight.current = false;
      setConflictError(describeConflictCheckError(err?.response?.data?.message, 'Could not start the check. Try again.'));
      setConflictLoading(false);
    }
  };

  useEffect(() => {
    if (!conflictCheckId) return;
    const run = conflictRun.current;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = conflictStartedAt ?? Date.now();
    const poll = async () => {
      try {
        const c = await supportAgentApi.getKbConflictCheck(conflictCheckId);
        if (cancelled || run !== conflictRun.current) return;
        setConflictCheck(c);
        setConflictElapsed(Math.round((Date.now() - startedAt) / 1000));
        if (KB_CONFLICT_TERMINAL_STATES.has(c.state)) {
          conflictInFlight.current = false;
          setConflictLoading(false);
          return;
        }
        if (Date.now() - startedAt > CONFLICT_POLL_MAX_MS) {
          conflictInFlight.current = false;
          setConflictLoading(false);
          setConflictError('Still running after 15 minutes. Check status again to resume waiting for this check.');
          return;
        }
        timer = setTimeout(poll, CONFLICT_POLL_INTERVAL_MS);
      } catch (err: any) {
        if (cancelled || run !== conflictRun.current) return;
        conflictInFlight.current = false;
        setConflictLoading(false);
        setConflictError(describeConflictCheckError(err?.response?.data?.message, 'Could not retrieve the check. Try checking its status again.'));
      }
    };
    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [conflictCheckId, conflictStartedAt]);

  const handleDisposition = async (findingId: string, action: KbConflictDispositionAction, reason?: string) => {
    if (!conflictCheckId) return;
    const run = conflictRun.current;
    setDispositionBusy(findingId);
    try {
      const { disposition } = await supportAgentApi.setKbConflictFindingDisposition(conflictCheckId, findingId, {
        action,
        ...(reason ? { reason } : {}),
      });
      if (run !== conflictRun.current) return;
      setConflictCheck((prev) =>
        prev ? { ...prev, findings: (prev.findings ?? []).map((f) => (f.id === findingId ? { ...f, disposition } : f)) } : prev,
      );
    } catch (err: any) {
      if (run !== conflictRun.current) return;
      // Reported ONCE, where the action was taken: the card (or its reason
      // dialog, which keeps its input until the write succeeds) shows this
      // message. The drawer-level alert is for the check's own lifecycle —
      // start, poll — so a failed decision neither lands there nor clears it.
      throw new ConflictCheckUserError(
        describeConflictCheckError(err?.response?.data?.message, 'Could not record the decision. Try again.'),
      );
    } finally {
      if (run === conflictRun.current) setDispositionBusy(null);
    }
  };

  const handleLoadArticle = async (docId: string) => {
    const d = await supportAgentApi.getKbDoc(docId);
    return { title: d.title, body: d.body };
  };

  const handleDownloadOriginal = async () => {
    if (!doc) return;
    setEditorError(null);
    try {
      const { url } = await supportAgentApi.getKbSourceUrl(doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setEditorError(err?.response?.data?.message || err.message);
    }
  };

  // ── upload flow: file picker -> upload-url -> raw PUT -> register-upload ──
  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    if (!contentTypeForFilename(file.name)) {
      setUploadError(`Unsupported file type. Allowed: ${KB_UPLOAD_ALLOWED_TYPES.join(', ')}`);
      return;
    }
    // Check the size cap client-side BEFORE we ever request a presigned
    // URL — no point making that round trip for a file the server (and the
    // extraction pipeline) will reject anyway.
    const sizeError = kbUploadSizeError(file);
    if (sizeError) {
      setUploadError(sizeError);
      return;
    }
    setUploadError(null);
    setPendingFile(file);
    setUploadTitle(stripExtension(file.name));
    setUploadDialogOpen(true);
  };

  const handleCancelUpload = () => {
    setUploadDialogOpen(false);
    setPendingFile(null);
  };

  const handleConfirmUpload = async () => {
    if (!pendingFile) return;
    const contentType = contentTypeForFilename(pendingFile.name);
    if (!contentType) {
      setUploadError('Unsupported file type.');
      return;
    }
    // Re-check the size cap here too — handleFileSelected already checked
    // it, but this is the actual point of no return before we mint a
    // presigned URL, so guard it again defensively.
    const sizeError = kbUploadSizeError(pendingFile);
    if (sizeError) {
      setUploadError(sizeError);
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const { url, key } = await supportAgentApi.createKbUploadUrl({
        filename: pendingFile.name,
        contentType,
        fileSize: pendingFile.size,
      });
      await uploadFileToPresignedUrl(url, pendingFile, contentType);
      const created = await supportAgentApi.registerKbUpload({
        key,
        title: uploadTitle.trim() || stripExtension(pendingFile.name),
      });
      setUploadDialogOpen(false);
      setPendingFile(null);
      loadList();
      openEditDoc(created.id);
    } catch (err: any) {
      setUploadError(err?.response?.data?.message || err.message);
    } finally {
      setUploading(false);
    }
  };

  const versionEntries: VersionHistoryEntry[] = (doc?.versions ?? []).map((v, idx) => {
    const docId = doc?.id;
    return {
      key: `${v.updatedAt}-${idx}`,
      updatedBy: v.updatedBy,
      updatedAt: v.updatedAt,
      summary: (
        <>
          <br />
          &ldquo;{v.title}&rdquo; · {v.status}
          {v.tags?.length ? ` · ${v.tags.join(', ')}` : ''}
        </>
      ),
      onRestore: () => handleRestoreVersion(idx),
      restoreDisabledReason: restoring !== null ? 'Restoring…' : undefined,
      // Fetches the full snapshot (incl. body) for the "View" dialog — the
      // summary above only ever has title/tags/status, never body (see
      // support-agent-api.ts).
      onView: docId ? () => supportAgentApi.getKbDocVersion(docId, idx) : undefined,
    };
  });

  if (view.mode === 'list') {
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={2}>
          <TextField
            size="small"
            placeholder="Search title or tags…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          <Stack direction="row" spacing={1}>
            <input
              ref={fileInputRef}
              type="file"
              accept={UPLOAD_ACCEPT}
              style={{ display: 'none' }}
              onChange={handleFileSelected}
            />
            <Button startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()}>
              Upload file
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={(e) => setTemplateMenuAnchor(e.currentTarget)}>
              New doc
            </Button>
            <Menu anchorEl={templateMenuAnchor} open={!!templateMenuAnchor} onClose={() => setTemplateMenuAnchor(null)}>
              {KB_TEMPLATES.map((t) => (
                <MenuItem key={t.key} onClick={() => openNewDoc(t.body)}>
                  <Stack>
                    <Typography variant="body2" fontWeight={600}>
                      {t.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.description}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        </Stack>

        {listError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {listError}
          </Alert>
        )}
        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated by</TableCell>
                <TableCell>Updated at</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={20} sx={{ my: 2 }} />
                  </TableCell>
                </TableRow>
              )}
              {!listLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No knowledge base docs yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!listLoading &&
                items.map((item) => (
                  <TableRow key={item.id} hover sx={{ cursor: 'pointer' }} onClick={() => openEditDoc(item.id)}>
                    <TableCell>{item.title}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {item.tags.map((tag) => (
                          <Chip key={tag} label={tag} size="small" />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip label={item.status} size="small" color={STATUS_COLOR[item.status] ?? 'default'} />
                    </TableCell>
                    <TableCell>{item.updatedBy}</TableCell>
                    <TableCell>{new Date(item.updatedAt).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => {
              setLimit(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>

        <Dialog open={uploadDialogOpen} onClose={handleCancelUpload}>
          <DialogTitle>Upload &ldquo;{pendingFile?.name}&rdquo;</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              This creates a draft doc pointing at the uploaded file. Its body says extraction is pending until the
              extraction pipeline fills it in — you can also just write the body yourself in the meantime.
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              label="Title"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              disabled={uploading}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelUpload} disabled={uploading}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleConfirmUpload} disabled={uploading || !uploadTitle.trim()}>
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={backToList} size="small" aria-label="Back to list">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">{doc ? 'Edit doc' : 'New doc'}</Typography>
        {doc && <Chip label={doc.status} size="small" color={STATUS_COLOR[doc.status] ?? 'default'} sx={{ ml: 1 }} />}
        <Box sx={{ flexGrow: 1 }} />
        {doc && (
          <Button startIcon={<HistoryIcon />} onClick={() => setVersionDrawerOpen(true)}>
            Version history ({doc.versions.length})
          </Button>
        )}
        {doc?.sourceFileKey && (
          <Button startIcon={<DownloadIcon />} onClick={handleDownloadOriginal}>
            Download original
          </Button>
        )}
        {doc && doc.status !== 'archived' && (
          <Button color="error" startIcon={<ArchiveIcon />} onClick={() => setArchiveConfirmOpen(true)}>
            Archive
          </Button>
        )}
      </Stack>

      {editorError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {editorError}
        </Alert>
      )}
      {saveNotice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveNotice(null)}>
          {saveNotice}
        </Alert>
      )}
      {doc && isPendingExtraction(doc.body) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Extraction pending — this doc was created from an uploaded file and its body is still the placeholder
          text. The extraction pipeline will fill it in; you can also edit it by hand right now.
        </Alert>
      )}

      {editorLoading ? (
        <CircularProgress />
      ) : (
        <Stack spacing={2}>
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth required />
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={tags}
            onChange={(_, v) => setTags(v as string[])}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => <Chip variant="outlined" label={option} size="small" {...getTagProps({ index })} />)
            }
            renderInput={(params) => <TextField {...params} label="Tags" placeholder="Add a tag and press Enter" />}
          />
          <TextField
            select
            label="Audience"
            value={audience}
            onChange={(e) => setAudience(e.target.value as KbAudience)}
            sx={{ maxWidth: 360 }}
            helperText="Which product this article is about. Mobile app chats are never shown Safety Hub web app articles."
          >
            {(Object.keys(AUDIENCE_LABEL) as KbAudience[]).map((a) => (
              <MenuItem key={a} value={a}>
                {AUDIENCE_LABEL[a]}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Source"
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value as KbDocSourceType | '')}
              sx={{ minWidth: 260 }}
              helperText="Where this article's facts come from (optional)"
            >
              <MenuItem value="">Not recorded</MenuItem>
              {(Object.keys(SOURCE_TYPE_LABEL) as KbDocSourceType[]).map((t) => (
                <MenuItem key={t} value={t}>
                  {SOURCE_TYPE_LABEL[t]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Source reference"
              placeholder="Help-center URL, or file path(s) + commit"
              value={sourceRef}
              onChange={(e) => setSourceRef(e.target.value)}
              disabled={!sourceType}
              fullWidth
            />
          </Stack>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Body (markdown) — only PUBLISHED docs reach the bot.
            </Typography>
            <MDEditor value={body} onChange={(v) => setBody(v ?? '')} height={420} preview="live" />
          </Box>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" disabled={!title || saving !== null} onClick={() => handleSave('draft')}>
              {saving === 'draft' ? 'Saving…' : 'Save as draft'}
            </Button>
            <Button variant="contained" disabled={!title || saving !== null} onClick={() => handleSave('published')}>
              {saving === 'published' ? 'Publishing…' : 'Publish'}
            </Button>
            {isKbConflictCheckEnabled() && (
              <Button
                variant="outlined"
                color="secondary"
                disabled={!title || !body.trim() || conflictLoading}
                onClick={handleCheckConflicts}
              >
                {conflictLoading ? 'Checking…' : 'Check for conflicts'}
              </Button>
            )}
            {isKbConflictCheckEnabled() && conflictCheck && !conflictDrawerOpen && (
              <Button variant="text" onClick={() => setConflictDrawerOpen(true)}>
                Show findings ({(conflictCheck.findings ?? []).filter((f) => f.relation !== 'compatible').length})
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      <VersionHistoryDrawer
        open={versionDrawerOpen}
        onClose={() => setVersionDrawerOpen(false)}
        title="Version history"
        entries={versionEntries}
        emptyMessage="No prior versions yet — this is the first save."
      />

      {isKbConflictCheckEnabled() && (
        <KbConflictCheckDrawer
          open={conflictDrawerOpen}
          onClose={() => setConflictDrawerOpen(false)}
          check={conflictCheck}
          loading={conflictLoading}
          error={conflictError}
          elapsedSeconds={conflictElapsed}
          onRunAgain={handleCheckConflicts}
          onJumpToDraft={(target) => {
            setConflictDrawerOpen(false);
            // Let the drawer close before moving focus into the editor.
            setTimeout(() => selectDraftRange(target), 0);
          }}
          onLoadArticle={handleLoadArticle}
          onRefresh={conflictCheckId ? () => {
            if (conflictInFlight.current) return;
            conflictInFlight.current = true;
            setConflictError(null);
            setConflictLoading(true);
            setConflictStartedAt(Date.now());
          } : undefined}
          onDisposition={handleDisposition}
          dispositionBusy={dispositionBusy}
        />
      )}

      <Snackbar
        open={restoreSnackbarOpen}
        autoHideDuration={5000}
        onClose={() => setRestoreSnackbarOpen(false)}
        message="Version loaded — review and Save/Publish to restore"
      />

      <Dialog open={archiveConfirmOpen} onClose={() => setArchiveConfirmOpen(false)}>
        <DialogTitle>Archive this doc?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Archiving removes it from the bot&apos;s knowledge base immediately (its chunks are deleted). You can bring
            it back later by editing it back to draft or published.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveConfirmOpen(false)}>Cancel</Button>
          <Button color="error" disabled={archiving} onClick={handleArchive}>
            {archiving ? 'Archiving…' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
